import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ShoppingBasketOutlinedIcon from "@mui/icons-material/ShoppingBasketOutlined";
import styled from "styled-components";
import { Link, useLocation } from "react-router-dom";
import {
  assistantStatus,
  askShoppingAssistant,
} from "../../api/shoppingAssistant";
import { COUNTRY_NAME } from "../../config/market";
import { optimizeCloudinaryUrl, getOriginalCloudinaryUrl } from "../../utils/cloudinaryOptimize";

const STARTERS = [
  { label: "Find a meal", text: "Help me choose a meal" },
  { label: "Shop my grocery list", text: "I want to buy groceries" },
  {
    label: "Order with friends",
    text: "How do I order together with friends?",
  },
];
const isInternalPath = (path) =>
  typeof path === "string" &&
  /^\/(?!\/)/.test(path) &&
  !/[\\\s]/.test(path) &&
  !Array.from(path).some((c) => c.charCodeAt(0) < 32);
function ItemImage({ src }) {
  const [attempt, setAttempt] = useState(0);
  const original = getOriginalCloudinaryUrl(src);
  const thumbnail = optimizeCloudinaryUrl(original, { width: 128, height: 128, crop: "fit", quality: "auto:eco" });
  return src && attempt < 2 ? (
    <img src={attempt ? original : thumbnail} alt="" loading="lazy" onError={() => setAttempt(value => value + 1)} />
  ) : (
    <ShoppingBasketOutlinedIcon aria-hidden="true" />
  );
}

export default function ShoppingAssistant() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [aiReady, setAiReady] = useState(null);
  const [checking, setChecking] = useState(false);
  const request = useRef(null);
  const generation = useRef(0);
  const end = useRef(null);
  const input = useRef(null);
  const isHome = pathname === "/";

  useEffect(() => () => request.current?.abort(), []);
  useEffect(() => {
    setOpen(false);
  }, [pathname]);
  useEffect(() => {
    // Introduce the label once per page load. Leaving home or opening the
    // helper ends the introduction; returning home keeps the compact icon.
    if (!isHome) {
      setMinimized(true);
      return undefined;
    }
    if (minimized) return undefined;
    const timer = setTimeout(() => setMinimized(true), 4000);
    return () => clearTimeout(timer);
  }, [isHome, minimized]);
  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    setChecking(true);
    assistantStatus(controller.signal)
      .then(setAiReady)
      .catch(() => {
        if (!controller.signal.aborted) setAiReady(false);
      })
      .finally(() => {
        if (!controller.signal.aborted) setChecking(false);
      });
    return () => controller.abort();
  }, [open]);
  useEffect(() => {
    if (open && messages.length)
      end.current?.scrollIntoView?.({ block: "start" });
  }, [messages, busy, open]);

  const reset = () => {
    generation.current += 1;
    request.current?.abort();
    request.current = null;
    setMessages([]);
    setDraft("");
    setBusy(false);
    setError(null);
    input.current?.focus();
  };

  const send = async (text) => {
    const value = text.trim();
    if (busy || request.current || value.length < 2 || value.length > 800)
      return;
    const epoch = ++generation.current;
    const history = messages
      .filter((m) => m.role === "user")
      .map((m) => m.text)
      .slice(-6);
    setMessages((previous) => [...previous, { role: "user", text: value }]);
    setDraft("");
    setError(null);
    setBusy(true);
    const controller = new AbortController();
    request.current = controller;
    try {
      const reply = await askShoppingAssistant(
        value,
        history,
        controller.signal,
      );
      if (epoch !== generation.current) return;
      setAiReady(reply.mode === "ai");
      setMessages((previous) => [
        ...previous,
        {
          role: "assistant",
          text: reply.message,
          products: reply.products || [],
          links: reply.links || [],
          mode: reply.mode,
        },
      ]);
    } catch (err) {
      if (epoch !== generation.current || controller.signal.aborted) return;
      setError({
        message:
          err.response?.data?.message ||
          "I couldn't check the catalogue just now. Try again or use search.",
        query: value,
      });
    } finally {
      if (epoch === generation.current) {
        setBusy(false);
        request.current = null;
      }
    }
  };

  // Keep the conversation in memory, but show the helper only on the home page.
  if (!isHome) return null;
  return (
    <>
      <LauncherDock>
        <Launcher
          type="button"
          onClick={() => {
            setOpen(true);
            setMinimized(true);
          }}
          aria-label="Open Lusuku shopping assistant"
          aria-haspopup="dialog"
          aria-expanded={open}
          $compact={minimized}
        >
          <AutoAwesomeOutlinedIcon aria-hidden="true" />
          {!minimized && (
            <span>
              Shop with Ai
            </span>
          )}
        </Launcher>
        {!minimized && (
          <Minimize
            type="button"
            aria-label="Minimize shopping help"
            onClick={() => setMinimized(true)}
          >
            <CloseIcon fontSize="small" />
          </Minimize>
        )}
      </LauncherDock>
      <Panel
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="shopping-helper-title"
        maxWidth={false}
        transitionDuration={0}
      >
        <Header>
          <div className="identity">
            <span className="spark">
              <AutoAwesomeOutlinedIcon />
            </span>
            <div>
              <DialogTitle id="shopping-helper-title">
                Your Lusuku shopping helper
              </DialogTitle>
              <span className="status">
                {checking
                  ? "Checking availability…"
                  : aiReady
                    ? "AI assistance · Live catalogue"
                    : "Catalogue search"}
              </span>
            </div>
          </div>
          <div className="header-actions">
            <IconButton
              aria-label="Start a new conversation"
              onClick={reset}
              disabled={!messages.length && !draft}
            >
              <RestartAltIcon />
            </IconButton>
            <IconButton
              aria-label="Close shopping assistant"
              onClick={() => setOpen(false)}
            >
              <CloseIcon />
            </IconButton>
          </div>
        </Header>
        <DialogContent className="conversation">
          {!messages.length && (
            <Welcome>
              <span className="eyebrow">A little help choosing</span>
              <h3>What are you shopping for?</h3>
              <p>
                Find food and products on Lusuku in {COUNTRY_NAME}. Tell me what
                you need, or start here.
              </p>
              <div className="starters">
                {STARTERS.map((s) => (
                  <button
                    key={s.label}
                    type="button"
                    onClick={() => {
                      setDraft(s.text);
                      input.current?.focus();
                    }}
                  >
                    {s.label}
                    <ArrowForwardIcon aria-hidden="true" />
                  </button>
                ))}
              </div>
              <p className="hint">
                A product name and a per-item budget help narrow the choices.
              </p>
            </Welcome>
          )}
          <div
            role="log"
            aria-label="Shopping conversation"
            aria-live="polite"
            aria-relevant="additions text"
          >
            {messages.map((m, index) => (
              <Message key={index} ref={index === messages.length - 1 ? end : null} $user={m.role === "user"}>
                <span className="speaker">
                  {m.role === "user" ? "You" : "Lusuku"}
                </span>
                <p>{m.text}</p>
                {m.mode === "catalogue" && (
                  <span className="fallback">
                    AI is unavailable. These are catalogue search results.
                  </span>
                )}
                {!!m.products?.length && (
                  <Products>
                    {m.products
                      .filter((p) => isInternalPath(p.path))
                      .map((p) => (
                        <Link
                          key={`${p.vertical}:${p.id}`}
                          to={p.path}
                          onClick={() => setOpen(false)}
                          aria-label={`View ${p.name} from ${p.vendorName}`}
                        >
                          <div className="photo">
                            <ItemImage src={p.imageUrl} />
                          </div>
                          <div className="product-copy">
                            <strong>{p.name}</strong>
                            <span>{p.vendorName}</span>
                            <b>{p.priceText}</b>
                            <span className="view">
                              {p.vertical === "restaurant"
                                ? "View menu & options"
                                : "View item"}{" "}
                              <ArrowForwardIcon aria-hidden="true" />
                            </span>
                          </div>
                        </Link>
                      ))}
                  </Products>
                )}
                {!!m.links?.length && (
                  <div className="reply-links">
                    {m.links
                      .filter((l) => isInternalPath(l.path))
                      .map((l) => (
                        <Link
                          key={l.path}
                          to={l.path}
                          onClick={() => setOpen(false)}
                        >
                          {l.label}
                          <ArrowForwardIcon aria-hidden="true" />
                        </Link>
                      ))}
                  </div>
                )}
              </Message>
            ))}
          </div>
          {busy && (
            <Working role="status">
              <span className="working-dot" />
              {aiReady
                ? "Understanding your request and checking the catalogue…"
                : "Checking the catalogue…"}
            </Working>
          )}
          {error && (
            <ErrorBox role="alert">
              <p>{error.message}</p>
              <button
                type="button"
                onClick={() => {
                  setDraft(error.query);
                  setError(null);
                  input.current?.focus();
                }}
              >
                Try again
              </button>
              <Link
                to={`/search?q=${encodeURIComponent(error.query)}`}
                onClick={() => setOpen(false)}
              >
                Use search
              </Link>
            </ErrorBox>
          )}
        </DialogContent>
        <Composer
          onSubmit={(e) => {
            e.preventDefault();
            send(draft);
          }}
        >
          <label htmlFor="shopping-helper-message">
            What would you like to find?
          </label>
          <div className="input-row">
            <textarea
              id="shopping-helper-message"
              ref={input}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              maxLength={800}
              rows={2}
              placeholder="Try “pizza” or “rice and cooking oil”"
              onKeyDown={(e) => {
                if (
                  e.key === "Enter" &&
                  !e.shiftKey &&
                  !e.nativeEvent.isComposing
                ) {
                  e.preventDefault();
                  send(draft);
                }
              }}
            />
            <button
              type="submit"
              aria-label="Send message"
              disabled={busy || draft.trim().length < 2}
            >
              <SendRoundedIcon />
            </button>
          </div>
          <p>Prices, options and delivery are confirmed in checkout.</p>
          <details>
            <summary>About this helper</summary>
            <p>
              When AI is available, your messages are sent to OpenAI to
              understand your request. Your saved address and cart are not
              shared. This chat stays in this tab until you start a new
              conversation or reload. You review and place orders through Lusuku
              checkout.
            </p>
          </details>
        </Composer>
      </Panel>
    </>
  );
}

const LauncherDock = styled.div`
  position: fixed;
  right: 20px;
  bottom: calc(20px + env(safe-area-inset-bottom, 0px));
  z-index: 1100;
  display: flex;
  align-items: center;
  gap: 4px;
  @media (max-width: 600px) {
    right: 12px;
    bottom: calc(12px + env(safe-area-inset-bottom, 0px));
  }
`;
const Launcher = styled.button`
  border: 1px solid #d3e1d5;
  border-radius: 28px;
  background: #fff;
  color: #203c34;
  min-height: 48px;
  min-width: 48px;
  padding: ${({ $compact }) => ($compact ? "10px" : "10px 14px")};
  box-shadow: 0 4px 18px rgba(21, 35, 42, 0.18);
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font:
    700 14px "Inter",
    "Segoe UI",
    sans-serif;
  > svg {
    color: #3d6a0b;
    width: 24px;
    height: 24px;
  }
  > span {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  &:hover {
    background: #f3f8ed;
  }
  &:focus-visible {
    outline: 3px solid #3d6a0b;
    outline-offset: 3px;
  }
  @media (max-width: 600px) {
    padding: ${({ $compact }) => $compact ? "10px" : "10px 14px"};
  }
`;
const Minimize = styled.button`
  width: 32px;
  height: 44px;
  border: 0;
  border-radius: 18px;
  color: #314c43;
  background: #fff;
  box-shadow: 0 3px 10px rgba(21, 35, 42, 0.1);
  cursor: pointer;
  display: grid;
  place-items: center;
  &:focus-visible {
    outline: 3px solid #3d6a0b;
  }
  @media (max-width: 600px) {
    display: none;
  }
`;
const Panel = styled(Dialog)`
  .MuiDialog-container {
    justify-content: flex-end;
    align-items: flex-end;
  }
  .MuiDialog-paper {
    width: 440px;
    max-width: calc(100% - 32px);
    height: 700px;
    max-height: calc(100dvh - 40px);
    margin: 20px;
    border-radius: 22px;
    color: #15232a;
    background: #fff;
    font-family: "Inter", "Segoe UI", sans-serif;
  }
  .conversation {
    padding: 20px;
    background: #f7f9f5;
    overflow-x: hidden;
  }
  @media (max-width: 600px) {
    .MuiDialog-paper {
      width: 100%;
      max-width: 100%;
      height: 90dvh;
      max-height: 90dvh;
      margin: 0;
      border-radius: 20px 20px 0 0;
    }
    .conversation {
      padding: 16px;
    }
  }
`;
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 6px;
  padding: 16px 12px 16px 18px;
  border-bottom: 1px solid #e4e9e0;
  .identity {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }
  .spark {
    background: #c5f23b;
    border-radius: 12px;
    padding: 8px;
    display: flex;
    color: #18342c;
  }
  h2 {
    font:
      700 15px/1.4 "Inter",
      sans-serif;
    padding: 0;
  }
  .status {
    font-size: 11px;
    color: #52634e;
  }
  .header-actions {
    display: flex;
    flex-shrink: 0;
  }
  .MuiIconButton-root {
    width: 36px;
    height: 40px;
    color: #43594c;
  }
  @media (max-width: 360px) {
    padding-left: 12px;
    .spark {
      display: none;
    }
    h2 {
      font-size: 14px;
    }
  }
`;
const Welcome = styled.div`
  padding: 8px 0;
  .eyebrow {
    color: #436b24;
    font-size: 12px;
    font-weight: 600;
  }
  h3 {
    margin: 10px 0;
    font-size: 24px;
    line-height: 1.25;
    letter-spacing: -0.5px;
  }
  p {
    margin: 0 0 18px;
    font-size: 14px;
    line-height: 1.6;
    color: #52615b;
  }
  .starters {
    display: grid;
    gap: 10px;
  }
  button {
    border: 1px solid #d8e0d2;
    border-radius: 12px;
    padding: 13px;
    min-height: 48px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    background: #fff;
    color: #253f31;
    font:
      600 13px "Inter",
      sans-serif;
    text-align: left;
    cursor: pointer;
  }
  button:hover {
    background: #edf5e3;
    border-color: #809969;
  }
  svg {
    width: 18px;
  }
  .hint {
    margin-top: 14px;
    font-size: 12px;
  }
`;
const Message = styled.div`
  margin: 0 0 16px;
  padding: 12px;
  border: 1px solid ${({ $user }) => ($user ? "#dce9cb" : "#e3e8df")};
  border-radius: 14px;
  background: ${({ $user }) => ($user ? "#edf5e3" : "#fff")};
  margin-left: ${({ $user }) => ($user ? "24px" : "0")};
  overflow-wrap: anywhere;
  .speaker {
    font-size: 11px;
    font-weight: 700;
    color: #4e6544;
  }
  p {
    white-space: pre-wrap;
    margin: 6px 0 0;
    font-size: 13px;
    line-height: 1.6;
  }
  .fallback {
    display: block;
    font-size: 11px;
    color: #59665b;
    line-height: 1.5;
    margin-top: 8px;
  }
  .reply-links {
    display: grid;
    gap: 8px;
    margin-top: 12px;
  }
  .reply-links a {
    display: flex;
    gap: 8px;
    align-items: center;
    color: #315b16;
    font-size: 12px;
    font-weight: 600;
    min-height: 36px;
  }
  .reply-links svg {
    width: 16px;
  }
`;
const Products = styled.div`
  display: grid;
  gap: 10px;
  margin-top: 14px;
  > a {
    display: flex;
    gap: 10px;
    color: #15232a;
    text-decoration: none;
    padding: 10px 0;
    border-top: 1px solid #e8ece4;
  }
  a:hover strong {
    text-decoration: underline;
  }
  .photo {
    width: 64px;
    height: 64px;
    background: #f3f6ed;
    border-radius: 10px;
    flex-shrink: 0;
    display: grid;
    place-items: center;
    overflow: hidden;
    color: #5b7348;
  }
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
  .product-copy {
    display: grid;
    gap: 4px;
    min-width: 0;
  }
  strong {
    font-size: 13px;
  }
  span {
    font-size: 11px;
    color: #59675e;
  }
  b {
    font-size: 13px;
  }
  .view {
    color: #315b16;
    display: flex;
    align-items: center;
    gap: 5px;
  }
  .view svg {
    width: 14px;
    height: 14px;
  }
`;
const Working = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #536447;
  font-size: 12px;
  padding: 10px 0;
  .working-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #5a7e32;
    flex-shrink: 0;
  }
`;
const ErrorBox = styled.div`
  padding: 12px;
  background: #fff5ef;
  border: 1px solid #e3bb9f;
  border-radius: 12px;
  font-size: 13px;
  p {
    margin: 0 0 8px;
  }
  button,
  a {
    color: #8d3b0b;
    font: inherit;
    background: none;
    border: 0;
    padding: 8px 8px 8px 0;
    text-decoration: underline;
    cursor: pointer;
  }
`;
const Composer = styled.form`
  border-top: 1px solid #e4e9e0;
  padding: 14px 18px calc(12px + env(safe-area-inset-bottom, 0px));
  flex-shrink: 0;
  label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 8px;
  }
  .input-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  textarea {
    resize: none;
    min-width: 0;
    width: 100%;
    background: #f7f9f5;
    border: 1px solid #cdd8c8;
    border-radius: 12px;
    padding: 10px;
    font:
      14px/1.5 "Inter",
      sans-serif;
    color: #15232a;
  }
  textarea:focus-visible {
    outline: 2px solid #51762f;
    outline-offset: 1px;
  }
  button {
    width: 44px;
    height: 44px;
    border: 0;
    border-radius: 50%;
    background: #c5f23b;
    color: #18342c;
    display: grid;
    place-items: center;
    flex-shrink: 0;
    cursor: pointer;
  }
  button:disabled {
    background: #e6ebdf;
    color: #6c7865;
    cursor: default;
  }
  p,
  summary {
    font-size: 10px;
    line-height: 1.6;
    color: #59675b;
    margin: 8px 0 0;
  }
  summary {
    cursor: pointer;
  }
  details p {
    margin-top: 5px;
  }
  @media (max-width: 600px) {
    textarea { font-size: 16px; }
  }
`;
